import React, { Suspense } from 'react';
import { LoadingSpinner } from 'components';
import Layout from 'components/Layout/layout';
import { LoginRoute } from 'components/common/LoginRoute';
import { ProtectedRoute } from 'components/common/ProtectedRoute';
import { ADMIN, RETAILER } from 'constants/roles';
import * as route from 'constants/routes';
import { UserProfileContent } from 'containers/UserProfile/userProfile';
import {
  AdminEditLookPage,
  AdminEventsPage,
  AdminLooksPage,
  BrandInformationPage,
  BrandManagerPage,
  BrandManagerThemeCustomizationPage,
  CategoriesPage,
  ChildProductsPage,
  ColorsPage,
  BannersPage,
  VideosPage,
  CustomStoresPage,
  DefaultLookImagePage,
  EditLookPage,
  EventsPage,
  FavouriteProductsPage,
  HowToHubPage,
  HowToHubRetailerPage,
  InventoriesPage,
  LocationGroupsPage,
  LocationPage,
  LocationsPage,
  OrderPage,
  OrdersPage,
  RetailerSalesPage,
  SuperUserPage,
  SuperUserOrdersPage,
  SuperUserRegionDetailPage,
  SuperUserRegionsPage,
  SuperUserTiersPage,
  SuperUserUsersPage,
  SuperUserImpersonationPage,
  WidgetShopifyGuidePage,
  ParentProductsPage,
  ProductsPage,
  RegionCheckoutPage,
  RegionDashboardPage,
  RegionDashboardLooksPage,
  RegionDashboardProductsPage,
  RegionDashboardOrdersPage,
  RegionDashboardOverviewPage,
  RegionInfoPage,
  RegionIntegrationsPage,
  RegionPageModulesPage,
  RegionPage,
  RegionSubscriptionsPage,
  RetailerPage,
  RetailerDetailsLooksPage,
  RetailerDetailsPage,
  RetailerDetailsProductsPage,
  RetailerLooksPage,
  RetailerLocationPage,
  RetailerLocationsPage,
  RetailersPage,
  SizesPage,
} from 'pages';
import TermsOfServicePage from 'pages/TermsOfService/TermsOfServicePage';
import PrivacyPolicyPage from 'pages/PrivacyPolicy/PrivacyPolicyPage';
import PageNotFound from 'pages/404/PageNotFound';
import ActivateAccountPage from 'pages/ActivateAccountPage/activateAccountPage';
import EditLocationGroupPage from 'pages/Locations/EditLocationGroupPage';
import ForgotPassword from 'pages/Login/ForgotPassword';
import LoginPage from 'pages/Login/LoginPage';
import OnboardingPage from 'pages/Onboarding/onboardingPage';
import RegisterApprovalPage from 'pages/Register/registerApprovalPage';
import RegisterPage from 'pages/Register/registerPage';
import { Navigate, Outlet, Route, Routes } from 'react-router-dom';

import CriticalErrorHandler from 'components/CriticalErrorHandler';
import { useAuthStore } from 'stores/authStore';
import { useAutoLogin } from 'api/auth/hooks';
import { useHandleAutoLoginError } from 'hooks/useHandleAutoLoginError';
import UserTour from 'components/UserTour/UserTour';
import WidgetsPage from 'pages/Widgets/WidgetsPage';
import WidgetCreatePage from 'pages/Widgets/WidgetCreatePage';
import StoreModulePage from 'pages/ModulePage/StoreModulePage';
import { useFetchLocation, useFetchRetailerLocation } from 'api/locations/hooks';
import LocationModulePage from 'pages/ModulePage/LocationModulePage';
import RegionModulePage from 'pages/ModulePage/RegionModulePage';
import WidgetEditPage from 'pages/Widgets/WidgetEditPage';
import RegionRedirect from 'components/common/RegionRedirect';
import MobileAppPromptModal from 'components/MobileAppPromptModal/MobileAppPromptModal';
import WhatsNewModal from 'components/WhatsNewModal/WhatsNewModal';

const ProtectedLayout = () => (
  <ProtectedRoute>
    <Layout>
      <CriticalErrorHandler>
        <MobileAppPromptModal />
        <Suspense>
          <Outlet />
        </Suspense>
        <UserTour />
        <WhatsNewModal />
      </CriticalErrorHandler>
    </Layout>
  </ProtectedRoute>
);

const App = () => {
  const { isLoading, isError } = useAutoLogin();
  useHandleAutoLoginError(isError);

  const role = useAuthStore((state) => state.role);
  const isSuperUser = useAuthStore((state) => state.isSuperUser);
  const isRetailer = role === RETAILER;
  const isAdmin = role === ADMIN;
  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Routes>
      <Route
        path={route.LOGIN}
        element={
          <LoginRoute>
            <LoginPage />
          </LoginRoute>
        }
      />
      <Route
        path={route.REGISTER}
        element={
          <LoginRoute>
            <RegisterPage />
          </LoginRoute>
        }
      />
      <Route
        path={route.ONBOARDING}
        element={
          <ProtectedRoute condition={isAdmin}>
            <OnboardingPage />
          </ProtectedRoute>
        }
      />
      <Route path={route.REGISTER_APPROVAL} element={<RegisterApprovalPage />} />
      <Route path={route.FORGOT_PASSWORD} element={<ForgotPassword />} />
      <Route path={route.ACTIVATE_ACCOUNT} element={<ActivateAccountPage />} />
      <Route path={route.TERMS_OF_SERVICE} element={<TermsOfServicePage />} />
      <Route path={route.PRIVACY_POLICY} element={<PrivacyPolicyPage />} />
      <Route path="/not_found" element={<PageNotFound />} />

      <Route element={<ProtectedLayout />}>
        <Route
          path="/"
          element={<Navigate to={isRetailer ? route.STORE : route.DASHBOARD} replace />}
        />

        <Route
          path={route.DASHBOARD}
          element={
            <ProtectedRoute condition={isAdmin}>
              <RegionDashboardPage />
            </ProtectedRoute>
          }
        >
          <Route index element={<RegionDashboardOverviewPage />} />
          <Route path={route.DASHBOARD_LOOKS} element={<RegionDashboardLooksPage />} />
          <Route path={route.DASHBOARD_PRODUCTS} element={<RegionDashboardProductsPage />} />
          <Route path={route.DASHBOARD_ORDERS} element={<RegionDashboardOrdersPage />} />
        </Route>

        <Route
          path={route.BRAND_MANAGER}
          element={
            <ProtectedRoute condition={isAdmin}>
              <BrandManagerPage />
            </ProtectedRoute>
          }
        >
          <Route path={route.BRAND_INFORMATION} element={<BrandInformationPage />} />
          <Route
            path={route.THEME_CUSTOMIZATION}
            element={<BrandManagerThemeCustomizationPage />}
          />
          <Route path={route.BRAND_ADMIN_USER} element={<UserProfileContent />} />
        </Route>

        <Route
          path={route.BANNERS}
          element={
            <ProtectedRoute condition={isAdmin}>
              <BannersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={route.VIDEOS}
          element={
            <ProtectedRoute condition={isAdmin}>
              <VideosPage />
            </ProtectedRoute>
          }
        />

        <Route
          element={
            <ProtectedRoute condition={isAdmin}>
              <ProductsPage />
            </ProtectedRoute>
          }
        >
          <Route path={route.PRODUCTS} />
          <Route path={route.PARENT_PRODUCTS} element={<ParentProductsPage />} />
          <Route path={route.CHILD_PRODUCTS} element={<ChildProductsPage />} />
          <Route path={route.COLORS} element={<ColorsPage />} />
          <Route path={route.SIZES} element={<SizesPage />} />
          <Route path={route.CATEGORIES} element={<CategoriesPage />} />
          <Route path={route.INVENTORIES} element={<InventoriesPage />} />
        </Route>

        <Route
          path={route.LOCATIONS}
          element={
            <ProtectedRoute condition={isAdmin}>
              <LocationsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={route.LOCATION}
          element={
            <ProtectedRoute condition={isAdmin}>
              <LocationPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={route.RETAILER_LOCATIONS}
          element={
            <ProtectedRoute condition={isRetailer}>
              <RetailerLocationsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={route.RETAILER_LOCATION}
          element={
            <ProtectedRoute condition={isRetailer}>
              <RetailerLocationPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={route.LOCATION_GROUPS}
          element={
            <ProtectedRoute condition={isAdmin}>
              <LocationGroupsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={route.RETAILER_LOCATION_GROUPS}
          element={
            <ProtectedRoute condition={isRetailer}>
              <LocationGroupsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={route.RETAILER_SALES}
          element={
            <ProtectedRoute condition={isRetailer}>
              <RetailerSalesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={route.LOCATION_GROUP}
          element={
            <ProtectedRoute condition={isAdmin}>
              <EditLocationGroupPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={route.RETAILER_LOCATION_GROUP}
          element={
            <ProtectedRoute condition={isRetailer}>
              <EditLocationGroupPage />
            </ProtectedRoute>
          }
        />
        <Route
          element={
            <ProtectedRoute condition={isSuperUser}>
              <SuperUserPage />
            </ProtectedRoute>
          }
        >
          <Route path={route.SUPER_USER} />
          <Route path={route.SUPER_USER_ORDERS} element={<SuperUserOrdersPage />} />
          <Route path={route.SUPER_USER_TIERS} element={<SuperUserTiersPage />} />
          <Route path={route.SUPER_USER_REGIONS} element={<SuperUserRegionsPage />} />
          <Route path={route.SUPER_USER_REGION_DETAIL} element={<SuperUserRegionDetailPage />} />
          <Route path={route.SUPER_USER_ACCESS} element={<SuperUserUsersPage />} />
          <Route path={route.SUPER_USER_IMPERSONATION} element={<SuperUserImpersonationPage />} />
        </Route>
        <Route
          path={route.ORDERS}
          element={
            <ProtectedRoute condition={isAdmin}>
              <OrdersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={route.ORDER}
          element={
            <ProtectedRoute condition={isAdmin}>
              <OrderPage />
            </ProtectedRoute>
          }
        />
        <Route path={route.PROFILE} element={<Navigate to={route.BRAND_ADMIN_USER} replace />} />
        <Route
          path={route.REGION_LOOKS}
          element={
            <ProtectedRoute condition={isAdmin}>
              <AdminLooksPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={route.RETAILERS}
          element={
            <ProtectedRoute condition={isAdmin}>
              <RetailersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={route.RETAILER}
          element={
            <ProtectedRoute condition={isAdmin}>
              <RetailerPage />
            </ProtectedRoute>
          }
        >
          <Route index element={<RetailerDetailsPage />} />
          <Route path={route.RETAILER_DETAIL_LOOKS} element={<RetailerDetailsLooksPage />} />
          <Route path={route.RETAILER_DETAIL_PRODUCTS} element={<RetailerDetailsProductsPage />} />
        </Route>
        <Route
          path={route.STORE}
          element={
            <ProtectedRoute condition={isRetailer}>
              <CustomStoresPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={route.STORE_MODULE_CREATE}
          element={
            <ProtectedRoute condition={isRetailer}>
              <StoreModulePage />
            </ProtectedRoute>
          }
        />
        <Route
          path={route.STORE_MODULE_EDIT}
          element={
            <ProtectedRoute condition={isRetailer}>
              <StoreModulePage />
            </ProtectedRoute>
          }
        />
        <Route
          path={route.LOCATION_MODULE_CREATE}
          element={
            <ProtectedRoute condition={isAdmin}>
              <LocationModulePage
                useLocationQuery={useFetchLocation}
                locationDetailRoute={route.LOCATION}
              />
            </ProtectedRoute>
          }
        />
        <Route
          path={route.LOCATION_MODULE_EDIT}
          element={
            <ProtectedRoute condition={isAdmin}>
              <LocationModulePage
                useLocationQuery={useFetchLocation}
                locationDetailRoute={route.LOCATION}
              />
            </ProtectedRoute>
          }
        />
        <Route
          path={route.RETAILER_LOCATION_MODULE_CREATE}
          element={
            <ProtectedRoute condition={isRetailer}>
              <LocationModulePage
                useLocationQuery={useFetchRetailerLocation}
                locationDetailRoute={route.RETAILER_LOCATION}
              />
            </ProtectedRoute>
          }
        />
        <Route
          path={route.RETAILER_LOCATION_MODULE_EDIT}
          element={
            <ProtectedRoute condition={isRetailer}>
              <LocationModulePage
                useLocationQuery={useFetchRetailerLocation}
                locationDetailRoute={route.RETAILER_LOCATION}
              />
            </ProtectedRoute>
          }
        />
        <Route
          path={route.REGION_PAGE_MODULE_CREATE}
          element={
            <ProtectedRoute condition={isAdmin}>
              <RegionModulePage />
            </ProtectedRoute>
          }
        />
        <Route
          path={route.REGION_PAGE_MODULE_EDIT}
          element={
            <ProtectedRoute condition={isAdmin}>
              <RegionModulePage />
            </ProtectedRoute>
          }
        />
        <Route
          path={route.EVENTS}
          element={
            <ProtectedRoute condition={isAdmin || isRetailer}>
              {isAdmin ? <AdminEventsPage /> : <EventsPage />}
            </ProtectedRoute>
          }
        />
        <Route
          path="/regions/:regionId/integrations"
          element={
            <ProtectedRoute condition={isAdmin}>
              <RegionRedirect targetPath={route.REGION_INTEGRATIONS} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/regions/:regionId/region-info"
          element={
            <ProtectedRoute condition={isAdmin}>
              <RegionRedirect targetPath={route.REGION_INFO} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/regions/:regionId/subscription-plans"
          element={
            <ProtectedRoute condition={isAdmin}>
              <RegionRedirect targetPath={route.REGION_SUBSCRIPTION_PLANS} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/regions/:regionId/checkout-config"
          element={
            <ProtectedRoute condition={isAdmin}>
              <RegionRedirect targetPath={route.REGION_CHECKOUT_CONFIG} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/regions/:regionId/default-look-image"
          element={
            <ProtectedRoute condition={isAdmin}>
              <RegionRedirect targetPath={route.REGION_DEFAULT_LOOK_IMAGE} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/regions/:regionId/how-to-hub"
          element={
            <ProtectedRoute condition={isAdmin}>
              <RegionRedirect targetPath={route.REGION_HOW_TO_HUB} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/regions/:regionId/page-modules"
          element={
            <ProtectedRoute condition={isAdmin}>
              <RegionRedirect targetPath={route.REGION_PAGE_MODULES} />
            </ProtectedRoute>
          }
        />
        <Route
          path={route.REGION}
          element={
            <ProtectedRoute condition={isAdmin}>
              <RegionPage />
            </ProtectedRoute>
          }
        >
          <Route path={route.REGION_INFO} element={<RegionInfoPage />} />
          <Route path={route.REGION_INTEGRATIONS} element={<RegionIntegrationsPage />} />
          <Route path={route.REGION_SUBSCRIPTION_PLANS} element={<RegionSubscriptionsPage />} />
          <Route path={route.REGION_CHECKOUT_CONFIG} element={<RegionCheckoutPage />} />
          <Route path={route.REGION_DEFAULT_LOOK_IMAGE} element={<DefaultLookImagePage />} />
          <Route path={route.REGION_HOW_TO_HUB} element={<HowToHubPage />} />
          <Route path={route.REGION_PAGE_MODULES} element={<RegionPageModulesPage />} />
        </Route>
        <Route
          path={route.RETAILER_LOOKS}
          element={
            <ProtectedRoute condition={isRetailer}>
              <RetailerLooksPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={route.REGION_LOOK_EDIT}
          element={
            <ProtectedRoute condition={isAdmin}>
              <AdminEditLookPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={route.LOOK_EDIT}
          element={
            <ProtectedRoute condition={isRetailer}>
              <EditLookPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={route.FAVORITE_PRODUCTS}
          element={
            <ProtectedRoute condition={isRetailer}>
              <FavouriteProductsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={route.HOW_TO_HUB_RETAILER}
          element={
            <ProtectedRoute condition={isRetailer}>
              <HowToHubRetailerPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={route.WIDGETS}
          element={
            <ProtectedRoute condition={isAdmin || isRetailer}>
              <WidgetsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={route.WIDGET_CREATE}
          element={
            <ProtectedRoute condition={isAdmin || isRetailer}>
              <WidgetCreatePage />
            </ProtectedRoute>
          }
        />
        <Route
          path={route.WIDGET_SHOPIFY_GUIDE}
          element={
            <ProtectedRoute condition={isAdmin || isRetailer}>
              <WidgetShopifyGuidePage />
            </ProtectedRoute>
          }
        />
        <Route
          path={route.WIDGET_EDIT}
          element={
            <ProtectedRoute condition={isAdmin || isRetailer}>
              <WidgetEditPage />
            </ProtectedRoute>
          }
        />

        <Route path="*" element={<PageNotFound />} />
      </Route>
    </Routes>
  );
};

export default App;
